// The actions slice of the stem ViewModel: the manual transform, the manual-transform flag and
// the reset actions — everything the panel invokes that mutates the Model without being part of
// selection.
import { computed } from 'vue';
import type { PlannerStore } from '@/views/3d-planner/store/plannerStore.types';
import { logger } from '@/util/logger';
import {
    formatTemplateStemDifferences,
    templateStemPropertiesAreEqual,
} from '@/views/3d-planner/template/templateComparison';
import { syncTemplate } from '@/views/3d-planner/template/manualTemplateState';
import {
    applyStemRotation,
    applyStemTranslation,
    logStemTransformChange,
    type StemTransformChange,
} from '@/views/3d-planner/fitting/stemTransform';

const log = logger();

// Whether the stem reset is disabled: no auto-template, or the current stem matches it.
function isStemResetDisabled(planner: PlannerStore): boolean {
    if (!planner.case?.autoTemplate || !planner.template) {
        return true;
    }
    const autoTemplate = planner.case.autoTemplate;
    const current = syncTemplate(planner.template);
    if (templateStemPropertiesAreEqual(current, autoTemplate)) {
        return true;
    }
    log.debug(
        [
            'Stem changes to automated surgical template are:',
            ...formatTemplateStemDifferences(current, autoTemplate),
        ].join('\n  '),
    );
    return false;
}

const transformStem = (planner: PlannerStore, change: Partial<StemTransformChange>) => {
    if (!planner.template || !planner.fittedStem?.axesLocal || !planner.case) {
        log.debug('Ignoring attempt to transform stem');
        return;
    }
    logStemTransformChange(change);

    const newTransform = planner.template.stemTransform.clone();
    applyStemTranslation(newTransform, change, planner.case.operationalSide);
    applyStemRotation(
        newTransform,
        change,
        planner.case.operationalSide,
        planner.fittedStem.axesLocal,
    );

    planner.template.stemTransform = newTransform;
};

export type StemActionsDeps = {
    planner: PlannerStore;
};

export const useStemActions = ({ planner }: StemActionsDeps) => ({
    transformStem: (change: Partial<StemTransformChange>) => transformStem(planner, change),
    isResetStemDisabled: computed(() => isStemResetDisabled(planner)),
    isResetStemTransformDisabled: computed(() => !planner.hasManualStemTransform),
    resetStem: () => planner.resetStem(),
    resetStemTransform: () => planner.resetStemTransform(),
});

export type StemActions = ReturnType<typeof useStemActions>;
